import { useEffect, useState } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { db } from '../../firebase/config';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { NativeSelect } from '../ui/select-native';
import PageHeader from './shell/PageHeader';

const EMPTY = { title: '', link: '', tournamentId: '', tournamentName: '' };

const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const re = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const m = url.match(re);
  return m && m[2].length === 11 ? m[2] : null;
};

const Field = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-heat">*</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const MediaManagement = () => {
  const [items, setItems] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'media'), orderBy('createdAt', 'desc')));
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      toast.error('Could not load media.', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const fetchTournaments = async () => {
    try {
      const snap = await getDocs(
        query(collection(db, 'tournaments'), orderBy('startDate', 'desc'))
      );
      setTournaments(snap.docs.map((d) => ({ id: d.id, name: d.data().name })));
    } catch {
      // silent — tournament picker just shows empty
    }
  };

  useEffect(() => {
    fetchMedia();
    fetchTournaments();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      title: m.title || '',
      link: m.link || '',
      tournamentId: m.tournamentId || '',
      tournamentName: m.tournamentName || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.link.trim()) {
      toast.error('Title and link are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        youtubeId: getYouTubeVideoId(form.link),
        updatedAt: new Date(),
      };
      if (editing) {
        await updateDoc(doc(db, 'media', editing.id), payload);
        toast.success('Media updated.');
      } else {
        await addDoc(collection(db, 'media'), { ...payload, createdAt: new Date() });
        toast.success('Media added.');
      }
      setDialogOpen(false);
      await fetchMedia();
    } catch (err) {
      toast.error('Save failed.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'media', deleteTarget.id));
      toast.success('Media deleted.');
      setDeleteTarget(null);
      await fetchMedia();
    } catch (err) {
      toast.error('Delete failed.', { description: err.message });
    }
  };

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow="Maps & media"
        title="Media library"
        description="Videos and highlights attached to tournaments. YouTube links get a thumbnail automatically."
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Add media
          </Button>
        }
      />

      {loading ? (
        <div className="divide-y divide-border/60 border-y border-border/60">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex h-24 animate-pulse items-center gap-6 px-2">
              <div className="h-16 w-28 bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-1/3 bg-muted" />
                <div className="h-3 w-2/3 bg-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border/60 px-6 py-16 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Empty
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            No media yet. Add your first YouTube clip.
          </p>
          <div className="mt-5">
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Add media
            </Button>
          </div>
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-3">
          {items.map((m) => (
            <li key={m.id}>
              <Card className="border-border/60 bg-background/40 transition-colors hover:border-foreground/40">
                <CardContent className="flex items-start gap-5 pt-6">
                  <div className="h-20 w-36 shrink-0 overflow-hidden rounded-md border border-border/60 bg-muted/20">
                    {m.youtubeId ? (
                      <img
                        src={`https://img.youtube.com/vi/${m.youtubeId}/mqdefault.jpg`}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        Link
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-display text-lg font-semibold">{m.title}</h3>
                    {m.tournamentName && (
                      <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                        {m.tournamentName}
                      </div>
                    )}
                    <a
                      href={m.link}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {m.link}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(m)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(m)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit media' : 'Add media'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="grid gap-5">
            <Field label="Title" required>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </Field>
            <Field label="Link" required hint="YouTube link will show a thumbnail.">
              <Input
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                placeholder="https://youtube.com/watch?v=…"
                required
              />
            </Field>
            <Field label="Tournament">
              <NativeSelect
                value={form.tournamentId}
                onChange={(e) => {
                  const t = tournaments.find((x) => x.id === e.target.value);
                  setForm({
                    ...form,
                    tournamentId: t?.id || '',
                    tournamentName: t?.name || '',
                  });
                }}
              >
                <option value="">— None —</option>
                {tournaments.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete media item?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Permanently removes{' '}
            <span className="font-medium text-foreground">{deleteTarget?.title}</span>.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaManagement;
